'use client'
import { useState, useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import { REVIEWERS_PER_PAGE } from 'src/_helpers/constants'
import { ReviewerItem } from 'src/components/items/ReviewerItem'
import Button from 'src/components/design-system/Button'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import LINKS from 'src/_helpers/links'
import { buildLocalizedPath } from 'src/_helpers/localizedLinks'
import { getColumnBorderClasses } from 'src/components/list/getTileBorderClasses'

gsap.registerPlugin(useGSAP)
const REVIEWER_COLUMNS = { t: 2, d: 3 }
const buildColumnBorders = (index) => `border-basic-gray8 ${getColumnBorderClasses(index, REVIEWER_COLUMNS)}`
export default function OurReviewersClient({ items, locale, routeContext = 'main' }) {
  const isDomainJournal = routeContext === 'journal-domain'
  const initPage = 1
  const translate = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const [currentPage, setCurrentPage] = useState(initPage)
  const newItemsRef = useRef([])

  const displayedItems = items.slice(0, currentPage * REVIEWERS_PER_PAGE)
  const hasMore = displayedItems.length < items.length

  useEffect(() => {
    newItemsRef.current = newItemsRef.current.slice(0, displayedItems.length)
  }, [displayedItems.length])

  useGSAP(() => {
    if (currentPage > initPage && newItemsRef.current.length > 0) {
      const startIndex = (currentPage - 1) * REVIEWERS_PER_PAGE
      const newElements = newItemsRef.current.slice(startIndex)

      gsap.fromTo(
        newElements,
        {
          opacity: 0,
          y: 20,
        },
        {
          opacity: 1,
          y: 0,
          duration: 0.6,
          stagger: 0.05,
          ease: 'power2.out',
        },
      )
    }
  }, [currentPage])

  const handleLoadMore = () => {
    setCurrentPage((prev) => prev + 1)
  }

  return (
    <div className="pt-64">
      <div className="grid gap-24 t:grid-cols-2 d:grid-cols-3">
        {displayedItems.map((item, i) => {
          const columnBorders = buildColumnBorders(i)
          const translation = getPreferredTranslation(item, locale) || {}
          const position = translation.academicPrefix
          const fullName = item.fullName
          const affiliation = item.affiliations
            ?.map((aff) => getPreferredTranslation(aff, locale)?.title)
            .filter((title) => title && title.trim())
            .join(', ')
          const desc = translation.text || null
          const url =
            routeContext === 'journal-domain'
              ? buildLocalizedPath(journalRoutes.JOURNALS.REVIEWER_DETAIL, locale, { slug: item.slug })
              : routeContext === 'monograph'
                ? buildLocalizedPath(LINKS.REVIEWERS.DETAIL, locale, { slug: item.slug })
                : null
          return (
            <div
              key={i}
              ref={(el) => {
                newItemsRef.current[i] = el
              }}
            >
              <ReviewerItem
                fullname={fullName}
                affiliation={affiliation}
                position={position}
                desc={url ? null : desc}
                url={url}
                className={`pb-16 ${columnBorders}`}
              />
            </div>
          )
        })}
      </div>
      {hasMore && (
        <Button
          onClick={handleLoadMore}
          className={`w-fit mt-24 d:mt-56 relative mx-auto block ${
            isDomainJournal
              ? '!border-basic-black !bg-transparent !text-basic-black hover:!bg-basic-gray8'
              : 'bg-transparent hover:bg-basic-white'
          }`}
          size="l"
        >
          <span className="flex items-center gap-x-8">{translate('T_SEE_MORE')}</span>
        </Button>
      )}
    </div>
  )
}
